import "./App.less";
import React, {useCallback, useEffect, useRef, useState} from "react";
import AppHeader from "./components/AppHeader";
import AppAside from "./components/AppAside";
import { Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Brand from "./components/pages/Brand";
import BrandTemplates from "./components/pages/BrandTemplates";
import LinkPages from "./components/pages/LinkPages";
import Designs from "./components/pages/Designs";
import OnlineMenus from "./components/pages/OnlineMenus";
import { env } from "./env/env";
import DashboardApi from "./api/DashboardApi";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  locationsState,
  memberState,
  mobileState,
  paymentJsLoadedState, ilJsLoadedState, freePlanDiscontinuedState
} from "./state/atoms";
import Logo from "./components/Logo";
import RouterService from "./service/RouterService";
import { DashboardDtoStripePlanTypeEnum, LocationDto, MemberDto, MemberDtoPlanEnum } from "./generated/dashboard-client";
import { AppContext } from "./AppContext";
import AccountDashboard from "./components/account/AccountDashboard";
import { UsersPage } from "./components/account/page/UsersPage";
import { SubscriptionPage } from "./components/account/page/SubscriptionPage";
import { BillingPage } from "./components/account/page/BillingPage";
import { EditLocationPage } from "./components/account/page/locations/EditLocationPage";
import { LocationsPage } from "./components/account/page/locations/LocationsPage";
import { ManageBrandPage } from "./components/account/page/ManageBrandPage";
import { BrandsPage } from "./components/account/page/BrandsPage";
import CookieNotification from "./components/CookieNotification";
import { PrintCheckoutPage } from "./components/print/PrintCheckoutPage";
import Storage from "./util/Storage";
import { isContentPath, isDSAPath, isLandingPath, isPrintPath } from "./util/utils";
import RegisterPage from "./components/register/RegisterPage";
import EnterpriseSsoPage from "./components/login/EnterpriseSsoPage";
import DrdLoginPage from "./components/login/DrdLoginPage";
import { logout } from "./service/AuthService";
import { PrintHistoryPageNew } from "./components/account/page/PrintHistoryPageNew";
import { PrintOrderDetailPage } from "./components/account/page/PrintOrderDetailPage";
import PasswordPage from "./components/PasswordPage";
import PasswordResetPage from "./components/PasswordResetPage";
import { getJSPath, loadJS } from "./util/ScriptLoader";
import ContentPage from "./components/content/ContentPage";
import BentoContent from "./components/content/BentoContent";
import AffiliateContent from "./components/content/AffiliateContent";
import AffiliateContentForm from "./components/content/AffiliateContentForm";
import AccountsApi from "./api/AccountsApi";
import ResourceShowcasePage from "./components/content/ResourceShowcasePage";
import DigitalAssetsPage from "./components/content/DigitalAssetsPage";
import AccessRemoved from "./components/pages/AccessRemoved";
import { updateMemberStatus } from "./service/AnalyticsService";
import ToastPartnerConnect from "./components/content/ToastPartnerConnectContent";
import OAuthPage from "./components/OAuthPage";
import Square from "./components/pages/Square";
import { ChatService } from "./service/ChatService";
import MenuChatDrawer from "./components/menu-chat/MenuChatDrawer";
import PressPage from "./components/content/PressPage";
import ToastIntegrationAnnouncement from "./components/content/article/ToastIntegrationAnnouncement";
import { SiteBanner } from "./components/SiteBanner";
import ShamrockPage from "./components/content/ShamrockPage";
import ShamrockMenuServices from "./components/content/ShamrockMenuServices";
import ShamrockResourceCenter from "./components/content/ShamrockResourceCenter";
import PartnersPage from "./components/content/PartnersPage";
import PartnerPage from "./components/content/PartnerPage";
import DSAManager from "./components/pages/DSAManager";
import MembersApi from "./api/MembersApi";
import UnpaidInvoiceModal from "./components/pages/UnpaidInvoiceModal";
import DisplayAppAnnouncement from "./components/content/article/DisplayAppAnnouncement";
import StreamOAuthPage from "./components/StreamOAuthPage";
import { MarketingBanner } from "./components/MarketingBanner";
import Popmenu from "./components/pages/Popmenu";
import TextBlockApi from "./api/TextBlockApi";
import {UserLocationsPermissions} from "./components/account/page/team/UserLocationsPermissions";
import { TeamPageV2 } from "./components/account/page/team/TeamPageV2";
import { PermissionPresets } from "./components/account/page/team/PermissionPresets";
import { BulkInvite } from "./components/account/page/team/BulkInvite";
import { MemberDtoRoleEnum } from "./generated/dashboard-client";
import CEOLetterModal from "./components/CEOLetterModal";
import FreePlanDiscontinuedModal from "./components/FreePlanDiscontinuedModal";
import DiningRDIntegration from "./components/account/page/DiningRDIntegration";
import {ItemLibraryPage} from "./components/item-library/ItemLibraryPage";
import { BrandManagementListPage } from "./components/account/page/brand-management/BrandManagementListPage";
import { BrandManagementDetailPage } from "./components/account/page/brand-management/BrandManagementDetailPage";
import { DataKitListPage } from "./components/account/page/data-kit/DataKitListPage";
import { DataKitDetailPage } from "./components/account/page/data-kit/DataKitDetailPage";
import { DataKitImportPage } from "./components/account/page/data-kit/DataKitImportPage";
import { DataKitMenuDataPage } from "./components/pages/DataKitMenuDataPage";
import Listings from "./components/pages/Listings";
import ListingsComingSoon from "./components/pages/ListingsComingSoon";
import { CompanyResourceLinkDto } from "./domain/CompanyResourceLinkDto";

const dbPath = env.dashboardPath;
let upgradeSuccess = false;

function App() {
  const location = useLocation();
  let navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const [searchParams, setSearchParams] = useSearchParams();

  const setMember = useSetRecoilState(memberState);
  const member = useRecoilValue(memberState);
  const [locations, setLocations] = useRecoilState(locationsState);
  const [isAppLoading, setAppLoading] = useState(true);
  const [dashboardLoaded, setDashboardLoaded] = useState(false);
  const [accessRemoved, setAccessRemoved] = useState(false);
  const [activeLocation, setActiveLocation] = useState({} as LocationDto);
  const [cartCount, setCartCount] = useState(0);
  const [paymentJsLoaded, setPaymentJsLoaded] = useRecoilState(paymentJsLoadedState);
  const setShowFreePlanDiscontinued = useSetRecoilState(freePlanDiscontinuedState);
  const [, setILJsLoaded] = useRecoilState(ilJsLoadedState);
  const [isSingleUserAcc, setIsSingleUserAcc] = useState(false);
  const [users, setUsers] = useState([] as MemberDto[]);
  const setMobile = useSetRecoilState(mobileState);
  const [showPopmenuWelcomeModal, setShowPopmenuWelcomeModal] = useState(false);

  const [unpaidInvoiceUrl, setUnpaidInvoiceUrl] = useState('');

  const [isMarketingBannerPage, setIsMarketingBannerPage] = useState(true);
  const [isSiteBanner, setIsSiteBanner] = useState(false);

  useEffect(() => {
    loadJS(getJSPath('mhm.umd.cjs'), () => {
      setPaymentJsLoaded(true);
    });
    loadJS(getJSPath('item-library.umd.cjs'), () => {
      setILJsLoaded(true);
    });

    // @ts-ignore
    window['AccountsApi'] = AccountsApi;
  }, [setPaymentJsLoaded, setILJsLoaded]);

  useEffect(() => {
    if (!member?.internalId || member?.status !== 1) return;

    MembersApi.getUnpaidInvoicePaymentPageUrl().then(resp => {
      setUnpaidInvoiceUrl(resp.data.invoice || '');
    });
  }, [member.internalId, member.status]);

  useEffect(() => {
    const popmenuWelcome = searchParams.get("popmenuWelcome");
    if ('true' === popmenuWelcome) {
      setShowPopmenuWelcomeModal(true);
      searchParams.delete("popmenuWelcome");
      setSearchParams(searchParams, { replace: true });
    }
  }, [setSearchParams, searchParams]);

  useEffect(() => {
    if (locations.length === 0) return;
    const locationId = RouterService.getLocationFromPath(location.pathname);
    let found = locations[0];
    const storedLoc = window.localStorage.getItem("activeLocation");
    if (locationId) {
      found = locations.find((it) => it.internalId === locationId)!;
    } else if (storedLoc) {
      found = locations.find((it) => it.internalId!.toString() === storedLoc)!;
    }

    if (!found) {
      found = locations[0];
    }

    window.localStorage.setItem("activeLocation", `${found.internalId}`);
    setActiveLocation(found);

    setMember((prev) => {
      const current = prev?.permissions?.[found.internalId!];
      if (!current) return prev;

      const next = {
        ...prev,
        viewOnly: current.viewOnly ?? false,
        itemLibraryOnly: current.itemLibraryOnly ?? false,
        dataOnly: current.dataOnly ?? false,
        downloads: current.downloadAndPublish ?? false,
        publish: (current.publish ?? false) || (current.downloadAndPublish ?? false),
        templates: current.createTemplate ?? false,
        updateDrd: current.updateDrd ?? false,
        orderPrint: current.orderPrint ?? false,
        manageBrand: prev.owner || prev.role === MemberDtoRoleEnum.Admin
          ? true
          : (current.manageBrand ?? false),
        holidayTemplates: current.holidayTemplates ?? false,
      };

      if (!prev.diningRDIntegration) {
        next.holidayTemplates = false;
      }

      if (!prev.diningRDIntegration || prev.owner) {
        next.updateDrd = false;
      }

      if (
        next.viewOnly === prev.viewOnly &&
        next.itemLibraryOnly === prev.itemLibraryOnly &&
        next.dataOnly === prev.dataOnly &&
        next.downloads === prev.downloads &&
        next.publish === prev.publish &&
        next.templates === prev.templates &&
        next.updateDrd === prev.updateDrd &&
        next.orderPrint === prev.orderPrint &&
        next.manageBrand === prev.manageBrand &&
        next.holidayTemplates === prev.holidayTemplates
      ) {
        return prev;
      }

      return next;
    });
  }, [location, locations, setMember]);

  useEffect(() => {
    const { pathname } = location;
    if (isLandingPath(pathname) || isPrintPath(pathname) || isDSAPath(pathname)) {
      setIsMarketingBannerPage(false);
    } else {
      setIsMarketingBannerPage(true);
    }
  }, [location]);

  useEffect(() => {
    TextBlockApi.getByName("Dashboard Notice").then((resp) => {
      if (resp.data && resp.data.details) {
        setIsSiteBanner(true);
      }
    })
  }, []);

  function setMemberOnSessionForIL(member: MemberDto | undefined) {
    window.sessionStorage.setItem('memberName', member?.name ?? "Anonymous");
    window.sessionStorage.setItem('memberId', `${member?.internalId ?? 0}`);
    window.sessionStorage.setItem('teamId', member?.teamId ?? '');
  }

  const checkDashboardStats = useCallback(() => {
    if (!paymentJsLoaded || dashboardLoaded) return;
    setDashboardLoaded(true);
    DashboardApi.status().then((resp) => {
      const { member, locations, oldLoginKey, dsaBeta, stripePlanType, enterpriseDisplayAccount } = resp.data;
      const { id, internalId, logged, email, name, teamId, plan, ownerId, status, printInvoicing, subscriptionInvoicing, partner, accountStatus } = member!;

      const REVOKED_STATE = 0;
      if (accountStatus === REVOKED_STATE) {
        logout().then(() => {
          window.location.href = env.basePath;
        });
      }

      setTimeout(() => {
        ChatService.init(member!);
        initFullstory(member!);
        initPinterest(member!);
        initPagesense(member!);

        DashboardApi.getIntercomData().then(async (resp) => {
          ChatService.addMemberDetails(resp.data);
        });
      });

      if ((plan === "REMOVED" && ownerId !== internalId) || locations?.length === 0) {
        setAccessRemoved(true);
        setAppLoading(false);
        return;
      }

      if (plan === "PAUSED" || plan === "DECLINED" || plan === "PAST_DUE" || plan === "CANCELLED") {
        const onDismiss = () => {
          if (upgradeSuccess) return;
          logout().then(() => {
            window.location.href = env.basePath;
          });
        }

        if (ownerId !== internalId) {
          onDismiss();
        } else {
          const props = {
            memberId: id!,
            onSuccess: () => {
              upgradeSuccess = true;
              window.location.reload();
            },
            onDismiss
          };

          if (plan === "PAUSED") window.MHM.ProModal.reactivateFromPaused(props);
          if (plan === "DECLINED") window.MHM.ProModal.reactivateFromDeclined(props);
          if (plan === "CANCELLED") window.MHM.ProModal.reactivateFromCancelled(props);
          if (plan === "PAST_DUE") window.MHM.ProModal.payPastDue(props);
        }
        return;
      }

      setMember({
        id: id ?? "",
        internalId: internalId ?? 0,
        teamId: teamId ?? "",
        email: email ?? "",
        name: name ?? "",
        logged: logged ?? false,
        owner: !ownerId || internalId === ownerId,
        role: member?.role ?? MemberDtoRoleEnum.Owner,
        plan: plan ?? MemberDtoPlanEnum.Free,
        oldLoginKey: oldLoginKey ?? "",
        status: status ?? 0,
        ownerId: member?.ownerId ?? 0,
        dsaBeta: dsaBeta ?? false,
        printInvoicing: printInvoicing ?? false,
        subscriptionInvoicing: subscriptionInvoicing ?? false,
        downloads: member?.downloads ?? false,
        publish: (member as any)?.publish ?? false,
        templates: member?.templates ?? false,
        printing: member?.printing ?? false,
        itemLibraryOnly: member?.itemLibraryOnly ?? false,
        dataOnly: member?.dataOnly ?? false,
        viewOnly: member?.viewOnly ?? false,
        manageBrand: (!ownerId || internalId === ownerId) || (member?.role === MemberDtoRoleEnum.Admin),
        updateDrd: false,
        orderPrint: true,
        holidayTemplates: false,
        partner: partner ?? "",
        permissions: member?.permissions ?? {},
        stripePlanType: stripePlanType ?? DashboardDtoStripePlanTypeEnum.Free,
        diningRDIntegration: member?.diningRDIntegration ?? false,
        diningRDIntegrationPro: stripePlanType === DashboardDtoStripePlanTypeEnum.ProDrd,
        diningRDIntegrationDesign: stripePlanType === DashboardDtoStripePlanTypeEnum.DesignDrd,
        enterpriseDisplayAccount: enterpriseDisplayAccount ?? false,
        showItemLibrary: member?.showItemLibrary ?? false,
        // memberState is built field by field rather than spread, so anything not named here is
        // dropped even when the payload carries it. dataKitBeta was missing, which hid every Data
        // Kit surface (the Menu Data and Data Kits nav entries, and the pages' own hasDataKitBeta
        // guard) for accounts that had the flag switched on. Cast because the generated MemberDto
        // has no such field - same reason `publish` above is cast - so TypeScript could not catch
        // the omission at either the write site or the `(member as any).dataKitBeta` read sites.
        dataKitBeta: (member as any)?.dataKitBeta ?? false,
        features: (member as any)?.features ?? [],
        companyResources: (resp.data as { companyResources?: CompanyResourceLinkDto[] }).companyResources ?? [],
      });

      setMemberOnSessionForIL(member);
      updateMemberStatus(internalId ?? 0, plan ?? "", plan ?? "", plan ?? "")

      if (plan === "FREE" && !checkForRegisterFlag()) {
        setShowFreePlanDiscontinued(true);
        setAppLoading(false);
        return;
      }

      if (member?.changePassword) {
        navigateRef.current("/account/password/reset");
        return;
      }

      AccountsApi.getUsersDetails(id!).then((resp) => {
        const users = resp.data.users || [];
        setIsSingleUserAcc((!ownerId || internalId === ownerId) && users.length === 0);
        setUsers(users);
      })

      setAppLoading(false);

      let uniqueIds: string[] = [];
      let uniqueLocs: LocationDto[] = [];
      locations!.forEach((loc) => {
        if (!uniqueIds.includes(loc.id!)) {
          uniqueIds.push(loc.id!);
          uniqueLocs.push(loc);
        }
      });

      uniqueLocs.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setLocations(uniqueLocs);

      const redirect = window.localStorage.getItem("redirect-path");
      if (redirect) {
        window.localStorage.removeItem("redirect-path");
        navigateRef.current(redirect!!);
      }
    }).catch((e) => {
      if (e.response.status === 401 || e.response.status === 504) {
        logout().then(() => {
          window.location.href = env.basePath;
        });
      }
      setAppLoading(false);
    });
  }, [setMember, setLocations, paymentJsLoaded, dashboardLoaded, setShowFreePlanDiscontinued])

  function initPinterest(member: MemberDto) {
    (window as any).pintrk('load', '2613736158945', {
      em: member.email,
    });
    (window as any).pintrk('page');
    // console.log('Pinterest initialized');
  }

  function initPagesense(member: MemberDto) {
    window.pagesense = window.pagesense || [];
    if (member.email) {
      window.pagesense.push(['identifyUser', `${member.email}`]);
    }

    let created = new Date(member.createdDate!);
    let startedCounting = new Date('09/27/2023');
    if (created > startedCounting && member.numLogins === 3) {
      console.log('should show NPS poll');
      window.pagesense.push(['trackEvent', 'Third visit']);
    }
  }

  function initFullstory(member: MemberDto) {
    try {
      // @ts-ignore
      if (!window['FS']) {
        console.log('=> FS not found');
        return;
      }

      // @ts-ignore
      window['FS'].identify(member.internalId, {
        email: member.email,
        displayName: member.name
      });
      // console.log('=> FS identified');
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    if (!searchParams.get('merchant_id') || !searchParams.get('code') || !searchParams.get('employee_id') || !searchParams.get('client_id')) return;

    navigate('/account/login' + window.location.search);
  }, [navigate, searchParams]);

  useEffect(() => {
    const popmenuWelcome = searchParams.get("popmenuWelcome");
    if ('true' === popmenuWelcome) {
      setShowPopmenuWelcomeModal(true);
      searchParams.delete("popmenuWelcome");
      setSearchParams(searchParams, { replace: true });
    }
  }, [setSearchParams, searchParams]);

  useEffect(() => {
    if (isLandingPath(location.pathname)) {
      setAppLoading(false);
      return;
    }

    if (isContentPath(location.pathname)) {
      if (Storage.getToken()) {
        checkDashboardStats();
        return;
      } else {
        setAppLoading(false);
        return;
      }
    }

    if (Storage.getToken()) {
      checkDashboardStats();
      return;
    } else {
      window.localStorage.setItem("redirect-path", window.location.pathname);
      window.location.href = `/account/login`;
    }
  }, [checkDashboardStats, location]);

  useEffect(() => {
    checkForRegisterFlag();
  }, []);

  function checkForRegisterFlag() {
    if (Storage.getRegisterFlag() === "true" &&
      window.location.pathname !== "/account/register" &&
      window.location.pathname.indexOf("/account/register") === -1
    ) {
      window.location.href = `/account/register`;
      return true;
    }
    return false;
  }

  const onLogin = useCallback(() => {
    setAppLoading(true);
  }, [setAppLoading]);

  useEffect(() => {
    if (window.outerWidth <= 991) {
      setMobile(true);
    }

    const handler = () => setMobile(window.innerWidth <= 991);

    window.addEventListener('resize', handler)
    return () => {
      window.removeEventListener('resize', handler);
    }
  }, [setMobile]);

  useEffect(() => {
    // console.log("isSiteBanner: " + isSiteBanner);
  }, [isSiteBanner]);

  return isAppLoading ? (
    <div className={"app-loading"}>
      <Logo />
    </div>
  ) : (
    <>
      {accessRemoved && <AccessRemoved />}
      {!!unpaidInvoiceUrl && <UnpaidInvoiceModal url={unpaidInvoiceUrl} />}
      {!accessRemoved && !unpaidInvoiceUrl && <AppContext.Provider
        value={{ location: activeLocation, setLocation: setActiveLocation }}
      >
        <div className="app">
          <CookieNotification />

          <MarketingBanner isVisiblePage={isMarketingBannerPage} />
          <SiteBanner setIsSiteBanner={setIsSiteBanner} />
          <AppHeader activeLocation={activeLocation} cartCount={cartCount} setCartCount={setCartCount} isMarketingBannerPage={isSiteBanner} users={users} />
          <CEOLetterModal/>
          <MenuChatDrawer />
          <FreePlanDiscontinuedModal/>

          <main
            className={`${(isLandingPath(location.pathname) || isContentPath(location.pathname)) ? "full" : ""} ${!isSiteBanner ? 'no-promo-banner' : ''}`}
          >
            <AppAside
              activeLocation={activeLocation}
              isMarketingBannerPage={isSiteBanner}
              users={users}
            />

            <div className={`container ${isPrintPath(location.pathname) || isDSAPath(location.pathname) ? " full-width" : ""}`} >
              <Routes>
                <Route path={dbPath + "/designs/:locationId"} element={<Designs isSingleUserAcc={isSingleUserAcc} showPopmenuWelcomeModal={showPopmenuWelcomeModal} users={users} />} />
                <Route path={dbPath + "/designs"} element={<Designs isSingleUserAcc={isSingleUserAcc} showPopmenuWelcomeModal={showPopmenuWelcomeModal} users={users} />} />
                <Route path={dbPath + "/brand-templates/:locationId"} element={<BrandTemplates />} />
                <Route path={dbPath + "/brand-templates"} element={<BrandTemplates />} />
                <Route path={dbPath + "/dsa/*"} element={<DSAManager activeLocation={activeLocation} isMarketingBannerPage={isSiteBanner} />} />
                <Route path={dbPath + "/listings-coming-soon"} element={<ListingsComingSoon />} />
                <Route path={dbPath + "/listings/:locationId"} element={<Listings />} />
                <Route path={dbPath + "/listings"} element={<Listings />} />
                <Route path={dbPath + "/online-menus"} element={<OnlineMenus activeLocation={activeLocation} />} />
                <Route path={dbPath + "/online-menus/:locationId"} element={<OnlineMenus activeLocation={activeLocation} />} />
                <Route path={dbPath + "/link-pages/:locationId"} element={<LinkPages activeLocation={activeLocation} />} />
                <Route path={dbPath + "/link-pages"} element={<LinkPages activeLocation={activeLocation} />} />
                <Route path={dbPath + "/brand/:locationId"} element={<Brand />} />
                <Route path={dbPath + "/brand"} element={<Brand />} />
                <Route path={dbPath + "/print/checkout"} element={<PrintCheckoutPage path={"cart"} setCartCount={setCartCount} />} />
                <Route path={dbPath + "/print/checkout/cart"} element={<PrintCheckoutPage path={"cart"} setCartCount={setCartCount} />} />
                <Route path={dbPath + "/print/checkout/empty"} element={<PrintCheckoutPage path={"empty"} />} />
                <Route path={dbPath + "/print/checkout/cart"} element={<PrintCheckoutPage path={"cart"} />} />
                <Route path={dbPath + "/print/checkout/shipping"} element={<PrintCheckoutPage path={"shipping"} />} />
                <Route path={dbPath + "/print/checkout/payment"} element={<PrintCheckoutPage path={"payment"} setCartCount={setCartCount} />} />
                <Route path={dbPath + "/print/checkout/success"} element={<PrintCheckoutPage path={"success"} />} />
                <Route path={dbPath + "/square"} element={<Square />} />
                <Route path={dbPath + "/popmenu"} element={<Popmenu />} />

                <Route path={dbPath + "/item-library"} element={<ItemLibraryPage />} />

                <Route path={dbPath + "/menu-data"} element={<DataKitMenuDataPage />} />

                <Route
                  path={dbPath + "/account"}
                  element={<AccountDashboard />}
                >
                  <Route path={"plan"} element={<SubscriptionPage activeLocation={activeLocation} />} />
                  <Route path={"billing"} element={<BillingPage />} />
                  <Route path={"users"} element={<UsersPage />} />
                  <Route path={"locations/:id"} element={<EditLocationPage />} />
                  <Route path={"locations"} element={<LocationsPage />} />
                  <Route path={"brands/new"} element={<ManageBrandPage />} />
                  <Route path={"brands/:id"} element={<ManageBrandPage />} />
                  <Route path={"brands"} element={<BrandsPage />} />
                  <Route path={"team"} element={<TeamPageV2 />} />
                  <Route path={"team/permission-presets"} element={<PermissionPresets />} />
                  <Route path={"team/bulk-invite"} element={<BulkInvite />} />
                  <Route path={"team/new"} element={<UserLocationsPermissions />} />
                  <Route path={"team/user/:id/locations"} element={<UserLocationsPermissions />} />
                  <Route path={"team/user/:id/permissions"} element={<UserLocationsPermissions />} />
                  <Route path={"print"} element={<PrintHistoryPageNew />} />
                  <Route path={"drd"} element={<DiningRDIntegration />} />
                  <Route path={"brand-management"} element={<BrandManagementListPage />} />
                  <Route path={"brand-management/:brandKitId"} element={<BrandManagementDetailPage />} />
                  <Route path={"data-kits"} element={<DataKitListPage />} />
                  <Route path={"data-kits/:kitId"} element={<DataKitDetailPage />} />
                  <Route path={"data-kits/:kitId/import"} element={<DataKitImportPage />} />
                  <Route path={`print/order/:id`} element={<PrintOrderDetailPage />} />
                  <Route path={""} element={<UsersPage />} />
                </Route>

                <Route
                  path={"/content"}
                  element={<ContentPage />}
                >
                  <Route path={"bento"} element={<BentoContent />} />
                  <Route path={"affiliate"} element={<AffiliateContent />} />
                  <Route path={"affiliate/form"} element={<AffiliateContentForm />} />
                  <Route path={"resource-showcase"} element={<ResourceShowcasePage />} />
                  <Route path={"assets"} element={<DigitalAssetsPage path={'logos'} />} />
                  <Route path={"assets/logos"} element={<DigitalAssetsPage path={'logos'} />} />
                  <Route path={"assets/colors"} element={<DigitalAssetsPage path={'colors'} />} />
                  <Route path={"assets/imagery"} element={<DigitalAssetsPage path={'imagery'} />} />
                  <Route path={"assets/iconography"} element={<DigitalAssetsPage path={'iconography'} />} />
                  <Route path={"toast-partner-connect"} element={<ToastPartnerConnect />} />
                  <Route path={"press"} element={<PressPage />} />
                  <Route path={"press/toast-integration-announcement"} element={<ToastIntegrationAnnouncement />} />
                  <Route path={"press/display-app-announcement"} element={<DisplayAppAnnouncement />} />
                  <Route path={"shamrock"} element={<ShamrockPage />} />
                  <Route path={"shamrock-menu-services"} element={<ShamrockMenuServices />} />
                  <Route path={"shamrock-resource-center"} element={<ShamrockResourceCenter />} />
                  <Route path={"partners"} element={<PartnersPage />} />
                  <Route path={"partner/:name"} element={<PartnerPage />} />
                </Route>

                <Route
                  path={dbPath}
                  element={<Designs isSingleUserAcc={isSingleUserAcc} showPopmenuWelcomeModal={showPopmenuWelcomeModal} users={users}/>}
                />
                <Route element={<Designs isSingleUserAcc={isSingleUserAcc} showPopmenuWelcomeModal={showPopmenuWelcomeModal} users={users} />} />

                <Route path={"/account/register/:inviteId"} element={<RegisterPage />} />
                <Route path={"/account/register"} element={<RegisterPage onLogin={onLogin} />} />
                <Route path={"/account/login"} element={<RegisterPage onLogin={onLogin} />} />
                <Route path={"/account/login/diningrd"} element={<DrdLoginPage onLogin={onLogin} />} />
                <Route path={"/account/sso/:provider"} element={<EnterpriseSsoPage />} />
                <Route path={"/account/oauth"} element={<OAuthPage />} />
                <Route path={"/account/stream/v1/authorize"} element={<StreamOAuthPage />} />
                <Route path={"/account/password"} element={<PasswordPage />} />
                <Route path={"/account/password/reset"} element={<PasswordResetPage />} />
              </Routes>
            </div>

            {/*<SurveyModal
              isVisible={isSurveyModal}
              onClose={() => setSurveyModal(false)}
            />*/}
          </main>
        </div>
      </AppContext.Provider>}
    </>
  );
}

export default App;
